/**
 * Jobsites API Service
 * Handles all jobsite/project-related API calls
 */

import { api } from './client';
import { ENDPOINTS } from '../../config/api.config';
import logger from '../../utils/logger';

/**
 * Fetch all jobsites for current organization
 */
export async function fetchJobsites(params = {}) {
  logger.info('[Jobsites API] Fetching jobsites', { params });
  const queryString = new URLSearchParams(params).toString();
  const endpoint = `${ENDPOINTS.projects.list}${queryString ? `?${queryString}` : ''}`;
  return api.get(endpoint);
}

/**
 * Fetch single jobsite by ID
 */
export async function fetchJobsite(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite', { jobsiteId });
  return api.get(ENDPOINTS.projects.get(jobsiteId));
}

/**
 * Create a new jobsite
 */
export async function createJobsite(data) {
  logger.info('[Jobsites API] Creating jobsite', { name: data.name });
  return api.post(ENDPOINTS.projects.create, data);
}

/**
 * Update a jobsite
 */
export async function updateJobsite(jobsiteId, data) {
  logger.info('[Jobsites API] Updating jobsite', { jobsiteId });
  return api.put(ENDPOINTS.projects.update(jobsiteId), data);
}

/**
 * Delete a jobsite
 */
export async function deleteJobsite(jobsiteId) {
  logger.info('[Jobsites API] Deleting jobsite', { jobsiteId });
  return api.delete(ENDPOINTS.projects.delete(jobsiteId));
}

/**
 * Fetch workers assigned to a jobsite
 */
export async function fetchJobsiteWorkers(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite workers', { jobsiteId });
  return api.get(ENDPOINTS.projects.workers(jobsiteId));
}

/**
 * Acknowledge a jobsite attention alert
 */
export async function acknowledgeJobsiteAlert(jobsiteId, alertId) {
  logger.info('[Jobsites API] Acknowledging jobsite alert', { jobsiteId, alertId });
  return api.patch(ENDPOINTS.projects.acknowledgeAlert(jobsiteId, alertId));
}

/**
 * Fetch jobsite safety data
 */
export async function fetchJobsiteSafety(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite safety', { jobsiteId });
  return api.get(ENDPOINTS.projects.safety(jobsiteId));
}

/**
 * Fetch jobsite compliance status
 */
export async function fetchJobsiteCompliance(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite compliance', { jobsiteId });
  return api.get(ENDPOINTS.projects.compliance(jobsiteId));
}

/**
 * Fetch jobsite activity feed
 */
export async function fetchJobsiteActivity(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite activity', { jobsiteId });
  return api.get(ENDPOINTS.projects.activity(jobsiteId));
}

/**
 * Fetch jobsite notifications
 */
export async function fetchJobsiteNotifications(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite notifications', { jobsiteId });
  return api.get(ENDPOINTS.projects.notifications(jobsiteId));
}

/**
 * Fetch jobsite contractors
 */
export async function fetchJobsiteContractors(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite contractors', { jobsiteId });
  return api.get(ENDPOINTS.projects.contractors(jobsiteId));
}

/**
 * Fetch jobsite documents
 */
export async function fetchJobsiteDocuments(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite documents', { jobsiteId });
  return api.get(ENDPOINTS.projects.documents(jobsiteId));
}

/**
 * Fetch jobsite photos
 */
export async function fetchJobsitePhotos(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite photos', { jobsiteId });
  return api.get(ENDPOINTS.projects.photos(jobsiteId));
}

/**
 * Fetch jobsite messages
 */
export async function fetchJobsiteMessages(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite messages', { jobsiteId });
  return api.get(ENDPOINTS.projects.messages(jobsiteId));
}

/**
 * Fetch jobsite crews
 */
export async function fetchJobsiteCrews(jobsiteId) {
  logger.info('[Jobsites API] Fetching jobsite crews', { jobsiteId });
  return api.get(ENDPOINTS.projects.crews(jobsiteId));
}

/**
 * Fetch jobsite suggestions (smart suggestions)
 */
export async function fetchJobsiteSuggestions() {
  logger.info('[Jobsites API] Fetching jobsite suggestions');
  return api.get(ENDPOINTS.projects.suggestions);
}

/**
 * Composed upcoming items for PM / safety (meetings, expiries, overdue, etc.)
 */
export async function fetchJobsiteUpcomingItems(jobsiteId, params = {}) {
  logger.info('[Jobsites API] Fetching upcoming items', { jobsiteId, params });
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const base = ENDPOINTS.projects.upcomingItems(jobsiteId);
  const endpoint = `${base}${queryString ? `?${queryString}` : ''}`;
  return api.get(endpoint);
}

/**
 * Fetch jobsite summary for dashboard
 * Returns aggregated statistics including counts, status distribution,
 * worker distribution, and upcoming deadlines
 */
export async function fetchJobsiteSummary() {
  logger.info('[Jobsites API] Fetching jobsite summary');
  return api.get(ENDPOINTS.projects.summary);
}

export default {
  fetchJobsites,
  fetchJobsite,
  createJobsite,
  updateJobsite,
  deleteJobsite,
  fetchJobsiteWorkers,
  acknowledgeJobsiteAlert,
  fetchJobsiteSafety,
  fetchJobsiteCompliance,
  fetchJobsiteActivity,
  fetchJobsiteNotifications,
  fetchJobsiteContractors,
  fetchJobsiteDocuments,
  fetchJobsitePhotos,
  fetchJobsiteMessages,
  fetchJobsiteCrews,
  fetchJobsiteSuggestions,
  fetchJobsiteSummary,
  fetchJobsiteUpcomingItems,
};
